import Axios from "../utils/http-request";
import { getRequestConfiguration } from "../utils/headerHelper";
import { AxiosErrorResponse } from "../interface/api_request";
import { JobEmployeeProps } from "../interface/job_board";
import {
  CreateEmployeeAppraisalProps,
  MobilityRecord,
} from "../interface/employment";

export const apiCreateEmployee = async (fieldValues: JobEmployeeProps) => {
  const formData = new URLSearchParams();
  // Append all form fields to FormData
  Object.entries(fieldValues).forEach(([key, value]) => {
    if (value !== undefined) {
      formData.append(key, value.toString());
    }
  });
  try {
    const uri = `/employments`;
    const cfg = await getRequestConfiguration({
      "Content-Type": "application/x-www-form-urlencoded",
    });
    return await Axios.post(uri, formData, cfg);
  } catch (e: any) {
    console.error(e);
    return {
      data: {},
      status: "error",
      msg: e?.response?.data?.message || e?.message,
    };
  }
};

export const apiGetEmploymentById = async (values: {
  employmentId: string;
}) => {
  try {
    const uri = `/employments/employment/${values.employmentId}`;
    const cfg = await getRequestConfiguration({
      "Content-Type": "application/x-www-form-urlencoded",
    });
    return await Axios.get(uri, cfg);
  } catch (e: any) {
    const error = e as AxiosErrorResponse;
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.msg ||
      error?.message ||
      "An error occurred";
    return {
      data: {},
      status: "error",
      msg: errorMessage,
    };
  }
};

export const apiGetMedicMobility = async (
  medicId: string,
): Promise<MobilityRecord[]> => {
  const cfg = getRequestConfiguration({
    "Content-Type": "application/json",
  });

  const res = await Axios.get<{ data: MobilityRecord[] }>(
    `/employments/mobility/medic/${medicId}`,
    cfg,
  );

  return res.data.data;
};

export const apiGetEmploymentsByFacility = async (
  page: string,
  filter: string,
) => {
  try {
    const cfg = await getRequestConfiguration({
      "Content-Type": "application/x-www-form-urlencoded",
    });
    const uri = `/employments/facility?page=${page}&${filter}`;
    return await Axios.get(uri, cfg);
  } catch (e: any) {
    const error = e as AxiosErrorResponse;
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.msg ||
      error?.message ||
      "An error occurred";
    return {
      data: {},
      status: "error",
      msg: errorMessage,
    };
  }
};

export const apiGetEmploymentsByMedicForFacility = async (medicId: string) => {
  try {
    const uri = `/employments/facility/${medicId}`;
    return await Axios.get(uri);
  } catch (e: any) {
    const error = e as AxiosErrorResponse;
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.msg ||
      error?.message ||
      "An error occurred";
    return {
      data: {},
      status: "error",
      msg: errorMessage,
    };
  }
};

export const apiGetEmploymentsByMedicForFacilityCopy = async () => {
  try {
    const uri = `/employments/medic`;
    return await Axios.get(uri);
  } catch (e: any) {
    const error = e as AxiosErrorResponse;
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.msg ||
      error?.message ||
      "An error occurred";
    return {
      data: {},
      status: "error",
      msg: errorMessage,
    };
  }
};

export const apiUpdateEmploymentStatus = async (fieldValues: {
  status: string;
  employmentId: string;
  reasons: string;
}) => {
  const formData = new URLSearchParams();
  try {
    const uri = `/employments/update-status/${fieldValues.employmentId}`;
    // Append all form fields to FormData
    Object.entries(fieldValues).forEach(([key, value]) => {
      if (value !== undefined) {
        formData.append(key, value.toString());
      }
    });
    const cfg = getRequestConfiguration({
      "Content-Type": "application/x-www-form-urlencoded",
    });
    return await Axios.put(uri, formData, cfg);
  } catch (e: any) {
    const error = e as AxiosErrorResponse;
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.msg ||
      error?.message ||
      "An error occurred";
    return {
      data: {},
      status: "error",
      msg: errorMessage,
    };
  }
};

// Employment Stats
export const apiGetEmploymentStats = async () => {
  try {
    const uri = `/employments/stats/general`;
    const cfg = await getRequestConfiguration({
      "Content-Type": "application/x-www-form-urlencoded",
    });
    return await Axios.get(uri, cfg);
  } catch (e: any) {
    const error = e as AxiosErrorResponse;
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.msg ||
      error?.message ||
      "An error occurred";
    return {
      data: {},
      status: "error",
      msg: errorMessage,
    };
  }
};

export const apiCreateEmployeeAppraisal = async (
  fieldValues: CreateEmployeeAppraisalProps,
  strengths: string[],
) => {
  const formData = JSON.stringify({ ...fieldValues, strengths });

  try {
    const uri = `/appraisals/appraisal`;
    const cfg = await getRequestConfiguration({
      "Content-Type": "application/json",
    });
    return await Axios.post(uri, formData, cfg);
  } catch (e: any) {
    console.error(e);
    return {
      data: {},
      status: "error",
      msg: e?.response?.data?.message || e?.message,
    };
  }
};

export const apiGetEmployeeTimesheet = async (
  employeeId: string,
  page: string = "1",
  size: string = "10",
  filters?: Record<string, string>,
) => {
  try {
    const queryParams = new URLSearchParams({
      page,
      size,
      ...(filters || {}),
    });

    const uri = `/employments/employment/${employeeId}/timesheet?${queryParams.toString()}`;
    const cfg = await getRequestConfiguration({
      "Content-Type": "application/x-www-form-urlencoded",
    });

    return await Axios.get(uri, cfg);
  } catch (e: any) {
    const error = e as AxiosErrorResponse;
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.msg ||
      error?.message ||
      "Failed to fetch timesheet";
    return {
      data: {},
      status: "error",
      msg: errorMessage,
    };
  }
};

// Add this function to your existing api/employment.ts file

export const apiAddExistingEmployees = async (fields: {
  token: string;
  employees: {
    email: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
    medic_type: string;
    gender?: string;
    state?: string;
    city?: string;
    dob?: string;
    folio_no?: string;
    years_of_experience?: number;
    specialties?: string;
    job_title: string;
    department?: string;
    employment_type: string;
    work_type: string;
    contract_duration?: string;
    start_date: string;
    end_date?: string;
    salary: number;
    benefits?: string[];
    note?: string;
  }[];
}) => {
  try {
    const uri = `/employments/add-existing-employee`;
    const cfg = await getRequestConfiguration({
      "Content-Type": "application/json",
      Authorization: `Bearer ${fields.token}`,
    });
    return await Axios.post(uri, { employees: fields.employees }, cfg);
  } catch (e: any) {
    const error = e as AxiosErrorResponse;
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.msg ||
      error?.message ||
      "An error occurred";
    return {
      data: {},
      status: "error",
      msg: errorMessage,
    };
  }
};
